import type { FC, ReactNode } from 'react'
import type { WorkItem } from '@/types'

import Link from 'next/link'
import { useId, useMemo, useState } from 'react'
import { ArrowLeft, Search, X } from 'lucide-react'
import SectionHeading from '@/components/landing/section-heading'
import CopyLines from '@/components/landing/copy-lines'
import WorkLogCard from '@/components/landing/work-log-card'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import { WORKS_COPY, WORKS_PAGE_COPY } from '@/constants/content'

interface WorksArchiveProps {
    works: Array<WorkItem>
}

interface NumberedWork {
    work: WorkItem
    number: number
    haystack: string
}

const normalize = (value: string): string => value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim()

// WORKS archive: the observatory's activity log as polaroid records with one quiet search line
// (company, project, work, role, description, tags). Records keep the same LOG number whatever
// the search.
const WorksArchive: FC<WorksArchiveProps> = ({ works }): ReactNode => {
    const searchId = useId()
    const [query, setQuery] = useState('')
    const numbered = useMemo<Array<NumberedWork>>(() => works.map((work, index) => ({
        work,
        number: index + 1,
        haystack: normalize([work.title, work.client, work.project, work.role, work.description, work.category, ...work.tags].filter(Boolean).join(' '))
    })), [works])

    const terms = normalize(query).split(' ').filter(Boolean)

    const results = numbered.filter(entry => terms.every(term => entry.haystack.includes(term)))

    const filtered = terms.length > 0

    const clearAll = (): void => setQuery('')

    return (
        <section aria-labelledby='works-archive-title' className='relative min-h-svh overflow-hidden bg-[linear-gradient(180deg,#f5f2ea_0%,#eef0ea_45%,#dcd8e4_100%)] pb-28 pt-32 lg:pt-40'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(29,42,51,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(29,42,51,0.045)_1px,transparent_1px)] bg-[size:72px_72px]' />
            <div aria-hidden='true' className='pointer-events-none absolute -right-[10vw] top-24 w-[44vw] max-w-[420px] opacity-70 lg:right-[-3vw] lg:w-[20vw]'>
                <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: '8.2s' }}>
                    <MahoniaSprig seed={93} className='-scale-x-100' />
                </div>
            </div>

            <div className='relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-5 lg:px-12'>
                <h1 className='sr-only'>WORKS 活動記録アーカイブ</h1>

                <nav aria-label='パンくずリスト' className='font-mono text-[10px] tracking-[0.28em] text-ink-soft'>
                    <ol className='flex items-center gap-2'>
                        <li><Link href='/' className='rounded hover:text-ink focus-visible:outline-2 focus-visible:outline-gold'>TOP</Link></li>
                        <li aria-hidden='true'>/</li>
                        <li aria-current='page' className='text-ink'>WORKS</li>
                    </ol>
                </nav>

                <div id='works-archive-title' className='flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out'>
                    <SectionHeading
                        index={WORKS_COPY.index}
                        kicker={WORKS_PAGE_COPY.kicker}
                        title={WORKS_PAGE_COPY.title}
                        ja={WORKS_PAGE_COPY.ja}
                        titleClassName='whitespace-nowrap lg:text-[clamp(3.5rem,7vw,7rem)]'
                    />
                    <p className='max-w-2xl text-[15px] leading-[2] tracking-[0.05em] text-ink/80'><CopyLines lines={WORKS_PAGE_COPY.lead} /></p>
                </div>

                <div role='search' className='flex w-full max-w-xl flex-col gap-2.5 animate-in fade-in slide-in-from-bottom-4 delay-150 duration-700 ease-out fill-mode-backwards'>
                    <label htmlFor={searchId} className='flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] text-ink-soft'>
                        <Sparkle className='size-2.5 text-gold' />
                        SEARCH
                        <span className='sr-only'>{WORKS_PAGE_COPY.searchLabel}</span>
                    </label>
                    <div className='group/search flex items-center gap-3 border-b border-ink/25 pb-2 transition-colors duration-500 focus-within:border-ink/70'>
                        <Search aria-hidden='true' className='size-4 shrink-0 text-ink/40 transition-colors duration-500 group-focus-within/search:text-ink' />
                        <input
                            id={searchId}
                            type='search'
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder={WORKS_PAGE_COPY.search}
                            autoComplete='off'
                            enterKeyHint='search'
                            className='min-h-10 w-full min-w-0 bg-transparent text-[15px] tracking-[0.04em] text-ink outline-none placeholder:text-ink/35 [&::-webkit-search-cancel-button]:hidden'
                        />
                        {query && (
                            <button
                                type='button'
                                onClick={clearAll}
                                aria-label='検索語を消す'
                                className='flex size-7 shrink-0 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-ink/5 hover:text-ink focus-visible:outline-2 focus-visible:outline-gold'
                            >
                                <X className='size-3.5' />
                            </button>
                        )}
                        <span aria-live='polite' className='shrink-0 font-mono text-[10px] tracking-[0.18em] text-ink-soft'>
                            {`${results.length} / ${works.length}`}
                        </span>
                    </div>
                </div>

                {results.length > 0
                    ? (
                        <ol className='grid grid-cols-1 gap-x-5 gap-y-8 pt-2 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-7 lg:gap-y-10'>
                            {results.map(({ work, number }) => (
                                <li key={work.id} className='animate-in fade-in slide-in-from-bottom-2 duration-500 ease-out'>
                                    <WorkLogCard work={work} number={number} />
                                </li>
                            ))}
                        </ol>
                    )
                    : (
                        <div className='flex flex-col items-center gap-4 rounded-[22px] border border-dashed border-ink/20 bg-mist/60 px-6 py-16 text-center'>
                            <Sparkle className='size-6 text-gold' />
                            <p className='text-sm tracking-[0.08em] text-ink/75'>{works.length === 0 ? WORKS_COPY.empty : WORKS_PAGE_COPY.empty}</p>
                            {filtered && (
                                <button type='button' onClick={clearAll} className='text-xs tracking-[0.12em] underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-gold'>
                                    {WORKS_PAGE_COPY.clear}
                                </button>
                            )}
                        </div>
                    )}

                <Link
                    href='/#works'
                    className='inline-flex w-fit items-center gap-2 self-center rounded-full px-5 py-2.5 font-mono text-[11px] tracking-[0.28em] ring-1 ring-ink/20 transition-colors duration-500 hover:bg-mist/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                >
                    <ArrowLeft className='size-3.5' />
                    {WORKS_PAGE_COPY.back}
                </Link>
            </div>
        </section>
    )
}

export default WorksArchive
